import { useMemo, cloneElement, isValidElement } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconGridDots, IconHome } from '@tabler/icons-react';
import { Title, Text, Badge } from '@/components/ui/core';
import { Stack, SimpleGrid, Box } from '@/components/ui/layout';
import { ActionIcon } from '@/components/ui';
import { Tooltip } from '@/components/ui/overlays';
import Modal from '@/components/ui/overlays/Modal';
import { useDomainAccess } from '@/hooks/useDomainAccess';
import { useDisclosure } from '@mantine/hooks';
import { useDomainPrimaryBadge } from '@/domain/badges/useDomainPrimaryBadge';
import type { DomainConfig } from '@/config/domains';

const EXCLUDED_DOMAINS = ['demo'];

export default function AppSwitcherMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const { accessibleDomains } = useDomainAccess();
  const [opened, { open, close }] = useDisclosure(false);

  const menuItems = useMemo(() => {
    return accessibleDomains
      .filter((domain) => !EXCLUDED_DOMAINS.includes(domain.key))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [accessibleDomains]);

  const handleNavigate = (path: string) => {
    close();
    navigate(path);
  };

  // Hide on home page
  const isHome = location.pathname === '/' || location.pathname === '/home';
  if (isHome) return null;

  return (
    <>
      <Tooltip label="Switch App" position="bottom">
        <ActionIcon
          variant="subtle"
          size="lg"
          aria-label="Switch App"
          onClick={open}
          style={{ color: 'var(--mantine-color-text)' }}
        >
          <IconGridDots size={20} />
        </ActionIcon>
      </Tooltip>

      <Modal
        opened={opened}
        onClose={close}
        backdropVariant="blur"
        size="lg"
        withCloseButton={false}
        padding="xl"
      >
        <Stack gap="lg">
          {/* Header */}
          <Stack gap={4} align="center">
            <Title order={3}>Switch App</Title>
            <Text size="sm" c="dimmed">
              Select an app to navigate to
            </Text>
          </Stack>

          {/* Home Card */}
          <Box
            onClick={() => handleNavigate('/home')}
            style={{
              cursor: 'pointer',
              padding: '12px 16px',
              borderRadius: 'var(--mantine-radius-md)',
              border: '1px solid var(--mantine-color-default-border)',
              transition: 'all 0.15s ease',
              display: 'flex',
              alignItems: 'center',
              gap: 16,
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
              e.currentTarget.style.backgroundColor = 'var(--mantine-color-default-hover)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
              e.currentTarget.style.backgroundColor = '';
              e.currentTarget.style.transform = '';
            }}
          >
            <Box
              style={{
                width: 52,
                height: 52,
                minWidth: 52,
                flexShrink: 0,
                borderRadius: 12,
                background: 'var(--mantine-color-blue-1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--mantine-color-blue-6)',
              }}
            >
              <IconHome size={28} />
            </Box>
            <Stack gap={0}>
              <Text size="sm" fw={600}>Home</Text>
              <Text size="xs" c="dimmed">Back to the main dashboard</Text>
            </Stack>
          </Box>

          {/* Apps Grid */}
          <Stack gap="xs">
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" pl={4}>
              Apps
            </Text>
            <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
              {menuItems.map((domain) => (
                <AppSwitcherTile
                  key={domain.key}
                  domain={domain}
                  onNavigate={() => handleNavigate(domain.basePath)}
                />
              ))}
            </SimpleGrid>
          </Stack>
        </Stack>
      </Modal>
    </>
  );
}

/**
 * AppSwitcherTile — single tile in the Switch App modal grid. Reads its
 * own live badge so React rules-of-hooks aren't violated by the parent's
 * `.map`. Live count overrides the static `domain.badge` fallback baked
 * into [config/domains.tsx](src/config/domains.tsx); when the live value
 * is null (no count or user lacks feature access) the tile falls back to
 * whatever the static config carries.
 */
function AppSwitcherTile({
  domain,
  onNavigate,
}: {
  domain: DomainConfig;
  onNavigate: () => void;
}) {
  const live = useDomainPrimaryBadge(domain.key);
  const badgeValue = live?.count ?? domain.badge ?? null;
  const badgeColor =
    live?.color ?? domain.badgeColor ?? (typeof domain.badge === 'number' ? 'red' : 'gray');

  return (
    <Box
      onClick={onNavigate}
      style={{
        cursor: 'pointer',
        padding: 12,
        borderRadius: 'var(--mantine-radius-md)',
        border: '1px solid var(--mantine-color-default-border)',
        transition: 'all 0.15s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        position: 'relative',
      }}
      onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.backgroundColor = 'var(--mantine-color-default-hover)';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--mantine-shadow-sm)';
      }}
      onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.backgroundColor = '';
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {badgeValue != null && (
        <Box style={{ position: 'absolute', top: 6, right: 6 }}>
          <Badge
            size="lg"
            variant="filled"
            color={badgeColor}
            circle={typeof badgeValue === 'number'}
          >
            {badgeValue}
          </Badge>
        </Box>
      )}

      <Box
        style={{
          width: 52,
          height: 52,
          borderRadius: 12,
          background: 'var(--mantine-color-indigo-1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--mantine-color-indigo-6)',
        }}
      >
        {isValidElement(domain.icon)
          ? cloneElement(domain.icon as React.ReactElement<{ size?: number }>, { size: 28 })
          : domain.icon}
      </Box>

      <Text size="xs" fw={500} ta="center" lineClamp={1}>
        {domain.label}
      </Text>
    </Box>
  );
}
